/**
 * S3 Direct Upload with Circular Cropper
 * Replaces UploadCare functionality
 * Supports 100MB file uploads with 1:1 and 3:4 aspect ratios
 */

console.log('🚀 Loading S3 Direct Uploader...');

// Print page size (mm) per poster size — same mapping as TransferData.create_img on the server
const S3_PRINT_PAGE_MM = {
  '13x18': [130, 180.75],
  '20x30': [210, 300.75],
  '21x30': [210, 300.75],
  '30x40': [300, 401],
  '60x40': [400, 601],
  '50x70': [500, 701]
};
const S3_DEFAULT_PAGE_MM = [400, 501];   // every other size uses the 400x500 template
const S3_TARGET_DPI = 200;
const S3_MAX_LONG_EDGE = 5000;
const S3_MAX_PIXELS = 16000000;          // iOS Safari refuses canvases above 16,777,216 px
const S3_PHOTO_SLOT_FRACTION = 0.3;      // circular photo is ~26% of the page width in *_img.html
const S3_MIN_PHOTO_PX = 1000;            // previous fixed size of the circular photo
const S3_JPEG_QUALITY = 0.9;

class S3DirectUploader {
  constructor(inputId, previewId, cropType = '1:1') {
    this.inputId = inputId;
    this.input = document.getElementById(inputId);
    this.previewId = previewId;
    this.preview = document.getElementById(previewId);
    this.cropType = cropType;
    this.cropper = null;
    this.originalFile = null;

    // Get text settings from label data attributes
    const label = document.querySelector(`label[for="${inputId}"]`);
    this.settings = {
      modalTitle: label?.dataset.modalTitle || 'Ajustează imaginea',
      modalHint: label?.dataset.modalHint || 'Ajustează zona pentru a decupa imaginea ta',
      uploadText: label?.dataset.uploadText || 'Încarcă imaginea',
      cancelText: label?.dataset.cancelText || 'Anulează',
      uploadingText: label?.dataset.uploadingText || 'Se încarcă...',
      uploadErrorText: label?.dataset.uploadErrorText || 'Încărcarea imaginii a eșuat. Te rugăm să încerci din nou.'
    };

    this.init();
  }

  init() {
    if (!this.input) {
      console.error(`Input element #${this.inputId} not found`);
      return;
    }

    this.input.addEventListener('change', (e) => this.handleFileSelect(e));
  }

  handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 104857600) {
      alert('File size must be less than 100MB');
      return;
    }

    this.originalFile = file;

    // Read file and show cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      this.showCropperModal(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  showCropperModal(imageSrc) {
    // Create modal
    const modal = this.createModal(imageSrc);
    document.body.appendChild(modal);

    // Initialize Cropper.js
    const cropperImage = document.getElementById('s3-cropper-image');
    const aspectRatio = this.cropType === '1:1' ? 1 : 3/4;

    this.cropper = new Cropper(cropperImage, {
      aspectRatio: aspectRatio,
      viewMode: 1,
      dragMode: 'move',
      autoCropArea: 0.8,
      restore: false,
      guides: true,
      center: true,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
      ready: () => {
        // Add circular overlay for 1:1 crops
        if (this.cropType === '1:1') {
          const cropBox = document.querySelector('.cropper-crop-box');
          if (cropBox) {
            cropBox.style.borderRadius = '50%';
            cropBox.style.overflow = 'hidden';
          }
          const face = document.querySelector('.cropper-face');
          if (face) {
            face.style.borderRadius = '50%';
          }
        }
      }
    });

    // Button handlers
    document.getElementById('s3-upload-btn').onclick = () => this.handleUpload();
    document.getElementById('s3-cancel-btn').onclick = () => this.closeModal();
    document.getElementById('s3-modal-overlay').onclick = () => this.closeModal();
  }

  async handleUpload() {
    const uploadBtn = document.getElementById('s3-upload-btn');
    uploadBtn.disabled = true;
    uploadBtn.textContent = this.settings.uploadingText;

    try {
      // Get cropped canvas, sized for the selected poster (never larger than the cropped source)
      const crop = this.cropper.getData(true);
      const size = S3DirectUploader.computeOutputSize(
        this.cropType, S3DirectUploader.getSelectedPosterSize(), crop.width, crop.height
      );
      const canvas = this.cropper.getCroppedCanvas({
        width: size.width,
        height: size.height,
        fillColor: '#fff',   // transparent PNG areas become white, not black, in the JPEG
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });

      // Always JPEG: wkhtmltopdf 0.12.6 cannot decode WebP (and PNG is needlessly large)
      canvas.toBlob(async (blob) => {
        if (!blob) {
          console.error('Upload error: canvas.toBlob returned null', size);
          alert(this.settings.uploadErrorText);
          uploadBtn.disabled = false;
          uploadBtn.textContent = '✓ ' + this.settings.uploadText;
          return;
        }
        await this.uploadToS3(blob);
      }, 'image/jpeg', S3_JPEG_QUALITY);

    } catch (error) {
      console.error('Upload error:', error);
      alert(this.settings.uploadErrorText);
      uploadBtn.disabled = false;
      uploadBtn.textContent = this.settings.uploadText;
    }
  }

  // Selected poster size, e.g. '30x40' (same DOM lookup as submit_form in myjs.js)
  static getSelectedPosterSize() {
    const active = document.querySelector('.state_1.active__picker');
    const state = active && active.dataset.state;
    if (!state) return '';
    const p = document.querySelector(`[data-state="${state}"].state_2 .active__picker p`);
    const parts = (p ? p.textContent : '').trim().split(' ');
    return (parts[1] || '').replace('cm', '');
  }

  // Output pixels for the cropped upload: ~200 DPI on the print page, capped for mobile canvas
  // limits, and never upscaled beyond the cropped source (cropW x cropH, natural pixels).
  static computeOutputSize(cropType, posterSize, cropW, cropH) {
    const ratio = cropType === '1:1' ? 1 : 3 / 4;   // width / height
    const page = S3_PRINT_PAGE_MM[(posterSize || '').trim()] || S3_DEFAULT_PAGE_MM;
    const pxPerMm = S3_TARGET_DPI / 25.4;

    let width;
    if (cropType === '1:1') {
      width = Math.max(S3_MIN_PHOTO_PX, page[0] * S3_PHOTO_SLOT_FRACTION * pxPerMm);
    } else {
      // background covers the whole page (background-size: cover)
      width = Math.max(page[0], page[1] * ratio) * pxPerMm;
    }

    width = Math.min(width, S3_MAX_LONG_EDGE * ratio);         // long edge is the height
    width = Math.min(width, Math.sqrt(S3_MAX_PIXELS * ratio));
    if (cropW > 0 && cropH > 0) {
      width = Math.min(width, cropW, cropH * ratio);
    }

    width = Math.max(1, Math.floor(width));
    const height = Math.max(1, Math.floor(width / ratio));
    return { width, height };
  }

  // S3 key-safe file name: ASCII letters/digits/-/_ only, always .jpg (the upload is re-encoded as JPEG)
  static sanitizeFileName(name) {
    const base = String(name || '').replace(/\.[^.]*$/, '');
    const slug = base
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);
    return (slug || 'upload') + '.jpg';
  }

  // Value for a CSS url("...") — quoted so spaces, parentheses and quotes don't break it
  static cssUrl(url) {
    return `url("${String(url).replace(/["\\]/g, '\\$&').replace(/[\n\r]/g, '')}")`;
  }

  // Verify the object is publicly readable by loading it as an image (works without bucket CORS)
  static loadsAsImage(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  static async verifyUpload(fileUrl, corsOk) {
    for (let attempt = 1; attempt <= 3; attempt++) {
      if (attempt > 1) await new Promise(r => setTimeout(r, 1000));
      if (corsOk) {
        try {
          const check = await fetch(fileUrl, { method: 'HEAD', mode: 'cors', cache: 'no-store' });
          if (check.ok) return true;
          console.warn(`S3 verify attempt ${attempt}/3: HTTP ${check.status}`);
          continue;
        } catch (e) {
          // HEAD not allowed by the bucket CORS rule — check by loading the image instead
          corsOk = false;
        }
      }
      if (await S3DirectUploader.loadsAsImage(fileUrl)) return true;
      console.warn(`S3 verify attempt ${attempt}/3: image did not load`);
    }
    return false;
  }

  async uploadToS3(blob) {
    const uploadBtn = document.getElementById('s3-upload-btn');

    try {
      // 1. Get pre-signed URL from Django backend
      console.log('→ Requesting pre-signed URL...');
      const presignedResponse = await fetch(
        `${proxy_loc}api/generate-upload-url/?file_name=${encodeURIComponent(S3DirectUploader.sanitizeFileName(this.originalFile.name))}&file_type=${encodeURIComponent(blob.type)}&crop_type=${this.cropType}`
      );

      if (!presignedResponse.ok) {
        throw new Error(`Failed to get upload URL: ${presignedResponse.status}`);
      }

      const presignedData = await presignedResponse.json();
      const { upload_url, fields, file_url } = presignedData;

      console.log('✓ Got pre-signed URL:', upload_url);
      console.log('✓ File will be uploaded to:', file_url);

      // 2. Prepare form data for S3 direct upload
      const formData = new FormData();
      Object.keys(fields).forEach(key => {
        formData.append(key, fields[key]);
      });
      formData.append('file', blob);

      // 3. Upload DIRECTLY to S3 (browser → S3, no backend!)
      console.log('→ Uploading to S3...');

      // CORS request so S3's real answer (204 on success) can be checked. A multipart POST is a
      // simple request, so the browser sends it even when the bucket has no CORS rule yet; then the
      // response is unreadable (TypeError) and we fall back to checking the stored object.
      let corsOk = true;
      try {
        const uploadResponse = await fetch(upload_url, {
          method: 'POST',
          body: formData,
          mode: 'cors'
        });
        if (!uploadResponse.ok) {
          throw new Error(`S3 rejected the upload: HTTP ${uploadResponse.status}`);
        }
      } catch (fetchError) {
        if (!(fetchError instanceof TypeError)) throw fetchError;
        console.warn('S3 CORS POST unreadable (no bucket CORS rule or network error), verifying object:', fetchError.message);
        corsOk = false;
      }

      // Verify the file actually landed on S3
      let verified = await S3DirectUploader.verifyUpload(file_url, corsOk);

      if (!verified && !corsOk) {
        // Last resort: the previous no-cors upload, then verify again
        try {
          await fetch(upload_url, { method: 'POST', body: formData, mode: 'no-cors' });
        } catch (fetchError) {
          console.warn('S3 no-cors POST threw:', fetchError.message);
        }
        verified = await S3DirectUploader.verifyUpload(file_url, false);
      }

      if (!verified) {
        throw new Error('Image upload could not be verified.');
      }

      console.log('✓ Upload verified on S3:', file_url);

      // 4. Update preview with S3 CDN URL
      this.updatePreview(file_url);

      // 5. Close modal
      this.closeModal();

    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('Error stack:', error.stack);

      alert(this.settings.uploadErrorText);

      if (uploadBtn) {
        uploadBtn.disabled = false;
        uploadBtn.textContent = '✓ ' + this.settings.uploadText;
      }
    }
  }

  updatePreview(fileUrl) {
    try {
      console.log(`→ Updating preview for input: ${this.inputId}`);
      console.log(`→ File URL: ${fileUrl}`);

      if (this.inputId === 'my-img-input') {
        // Circular image preview
        if (this.preview) {
          console.log('→ Setting preview image src...');
          this.preview.src = fileUrl;

          // Fade in using jQuery if available
          if (typeof $ !== 'undefined' && typeof $.fn.fadeIn === 'function') {
            $('#bg-img-new').fadeIn();
            console.log('✓ Used jQuery fadeIn');
          } else {
            // Fallback to vanilla JS
            this.preview.style.display = 'block';
            console.log('✓ Used vanilla JS display');
          }

          console.log('✓ Preview image updated');
        } else {
          console.warn('⚠️ Preview element not found for', this.previewId);
        }

        // ✅ Update global variable (used in form submission - replaces UploadCare cdnUrl)
        window.image_upload_file = fileUrl;
        console.log('✓ Global variable image_upload_file updated:', window.image_upload_file);

      } else if (this.inputId === 'my-bg-input') {
        // Background image preview
        const frameElement = document.querySelector('.custom-frame.bg-new');
        if (frameElement) {
          console.log('→ Setting background image...');
          frameElement.style.backgroundImage = S3DirectUploader.cssUrl(fileUrl);
          frameElement.style.backgroundSize = 'cover';
          frameElement.style.backgroundPosition = 'center';
          console.log('✓ Background image updated');
        } else {
          console.warn('⚠️ Background frame element not found');
        }

        // ✅ Store for background image submission
        window.bg_image_upload_file = fileUrl;
        console.log('✓ Global variable bg_image_upload_file updated:', window.bg_image_upload_file);
      }

      console.log('✅ Preview update complete!');

    } catch (error) {
      console.error('❌ Error updating preview:', error);
      console.error('Error stack:', error.stack);
    }
  }

  createModal(imageSrc) {
    const modal = document.createElement('div');
    modal.id = 's3-cropper-modal';
    modal.className = 's3-cropper-modal';
    modal.innerHTML = `
      <div class="s3-cropper-modal-overlay" id="s3-modal-overlay"></div>
      <div class="s3-cropper-modal-content">
        <h3>${this.cropType === '1:1' ? '🌟' : '🖼️'} ${this.settings.modalTitle}</h3>
        <p class="cropper-hint">${this.settings.modalHint}</p>
        <div class="s3-cropper-wrapper">
          <img id="s3-cropper-image" src="${imageSrc}" style="max-width: 100%;" />
        </div>
        <div class="s3-cropper-actions">
          <button id="s3-upload-btn" class="btn-s3-upload">✓ ${this.settings.uploadText}</button>
          <button id="s3-cancel-btn" class="btn-s3-cancel">✕ ${this.settings.cancelText}</button>
        </div>
      </div>
    `;
    return modal;
  }

  closeModal() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    const modal = document.getElementById('s3-cropper-modal');
    if (modal) {
      modal.remove();
    }

    // Reset file input
    if (this.input) {
      this.input.value = '';
    }
  }
}

// Initialize global variables for backward compatibility with existing code
window.image_upload_file = 'https://cdn.shopify.com/s/files/1/0511/8522/1811/files/download_9.png';
window.bg_image_upload_file = '';

console.log('✓ S3 Direct Uploader loaded');
